import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import aiUsageScoreService, { AIUsageScoreResponse } from '../../services/api/aiUsageScoreService';

// 状態の型定義
interface AIUsageScoreState {
  isLoading: boolean;
  error: string | null;
  organizationSummary: {
    organization_name: string;
    average_score: number;
    total_usage_count: number;
    total_saved_time_minutes: number;
    industry_ranking: string;
    feedback: string;
    top_agents: Array<{
      agent_id: number;
      name: string;
      count: number;
      usage_rate: number;
    }>;
    usage_count_change_percent: number;
    saved_time_change_percent: number;
  } | null;
  users: Array<{
    id: number;
    name: string;
    email: string;
    avatar: string | null;
    score: number;
    usage_count: number;
    saved_time_minutes: number;
    ranking: string;
    feedback: string;
    used_agents: Array<{
      agent_id: number;
      name: string;
      count: number;
      usage_rate: number;
    }>;
    usage_count_change_percent?: number;
    saved_time_change_percent?: number;
  }>;
  startDate: string | null;
  endDate: string | null;
}

// 非同期アクション：AI活用スコアの取得
export const fetchAIUsageScores = createAsyncThunk(
  'aiUsageScore/fetchAIUsageScores',
  async ({ startDate, endDate }: { startDate?: string; endDate?: string }, { rejectWithValue }) => {
    try {
      const response = await aiUsageScoreService.getAIUsageScores(startDate, endDate);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'AI活用スコアの取得に失敗しました');
    }
  }
);

// 初期状態
const initialState: AIUsageScoreState = {
  isLoading: false,
  error: null,
  organizationSummary: null,
  users: [],
  startDate: null,
  endDate: null,
};

// スライスの作成
const aiUsageScoreSlice = createSlice({
  name: 'aiUsageScore',
  initialState,
  reducers: {
    setDateRange: (state, action: PayloadAction<{ startDate: string | null, endDate: string | null }>) => {
      state.startDate = action.payload.startDate;
      state.endDate = action.payload.endDate;
    },
    clearAIUsageScores: (state) => {
      state.organizationSummary = null;
      state.users = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAIUsageScores.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAIUsageScores.fulfilled, (state, action: PayloadAction<AIUsageScoreResponse>) => {
        state.isLoading = false;
        state.organizationSummary = action.payload.organization_summary;
        state.users = action.payload.users;
      })
      .addCase(fetchAIUsageScores.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// アクションのエクスポート
export const { setDateRange, clearAIUsageScores } = aiUsageScoreSlice.actions;

// セレクタのエクスポート
export const selectAIUsageScoreIsLoading = (state: RootState) => state.aiUsageScore.isLoading;
export const selectAIUsageScoreError = (state: RootState) => state.aiUsageScore.error;
export const selectOrganizationSummary = (state: RootState) => state.aiUsageScore.organizationSummary;
export const selectUsers = (state: RootState) => state.aiUsageScore.users;
export const selectDateRange = (state: RootState) => ({
  startDate: state.aiUsageScore.startDate,
  endDate: state.aiUsageScore.endDate,
});

export default aiUsageScoreSlice.reducer;
